import { useParams } from "react-router-dom";
import AnswerSourceItem from "../ai/answer-source-item";
import CloseIcon from "../ui/icons/CloseIcon";
import { Text } from "../ui/text";
import useAnswerSideBarStore, { AnswerSideBarItem } from "@/stores/answer-side-bar";
import { lazy, Suspense, useEffect } from "react";
import PdfBadgeIcon from "../ui/icons/PdfBadgeIcon";
import CsvBadgeIcon from "../ui/icons/CsvBadgeIcon";

/* 탭 구조는 코드 하이라이터를 끌고 와서, 채팅 첫 화면에 싣지 않고 열 때 받는다 */
const KbCitationContent = lazy(() => import("./kb-citation-content"));

const FILE_EXTENSIONS = [".pdf", ".csv", ".png"];

const isFileItem = (item: AnswerSideBarItem) =>
  FILE_EXTENSIONS.some((ext) => item.title?.toLowerCase().endsWith(ext));

const getFileBadge = (title: string) => {
  const lower = title.toLowerCase();
  if (lower.endsWith(".pdf")) return <PdfBadgeIcon className="shrink-0" />;
  if (lower.endsWith(".csv")) return <CsvBadgeIcon className="shrink-0" />;
  const ext = title.split(".").pop()?.toUpperCase() ?? "FILE";
  return (
    <span className="inline-flex items-center justify-center rounded-[4px] bg-[#4A90D9] px-[6px] h-[24px] text-white text-[11px] font-bold shrink-0">
      {ext}
    </span>
  );
};

/** 출처 목록. 파일 출처를 위에 모으고 구분선 아래에 웹 출처를 보인다. 탭 모드의 External Ref. 탭도 이 목록을 쓴다 */
const ReferenceList = ({ items, splitFiles }: { items: AnswerSideBarItem[]; splitFiles: boolean }) => {
  const fileItems = splitFiles ? items.filter(isFileItem) : [];
  const refItems = splitFiles ? items.filter((item) => !isFileItem(item)) : items;

  return (
    <>
      {fileItems.length > 0 && (
        <>
          <div className="flex flex-col gap-[15px] mb-[16px]">
            {fileItems.map((item) => (
              <article key={item.id} className="hover:bg-gray-50">
                <div className="flex flex-col gap-1 p-2 rounded-lg">
                  <div className="flex items-center gap-[8px]">
                    <Text variant="label-lg" className="text-grey-0 shrink-0 text-[14px]">[{item.id}]</Text>
                    <div className="flex items-center gap-[10px] min-w-0">
                      {getFileBadge(item.title)}
                      <Text variant="body-regular" className="truncate">{item.title}</Text>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="border-t border-[#E8E8E8] mb-[16px]" />
        </>
      )}
      <div className="flex flex-col gap-[24px]">
        {refItems.map((item) => (
          <AnswerSourceItem key={item.id} item={item} index={item.id} />
        ))}
      </div>
    </>
  );
};

const AnswerSourceSideBar = () => {
  const { id } = useParams();
  const { isOpen, toggleAnswerSideBar, items, type, closeAnswerSideBar, isKb, knowledgeContent } =
    useAnswerSideBarStore();

  useEffect(() => {
    if (id) {
      closeAnswerSideBar();
    }
  }, [id]);

  const list = <ReferenceList items={items} splitFiles={type === "answer-reference"} />;

  return (
    /* 닫혀도 좌우 여백과 테두리가 남으면 오른쪽 끝에 투명한 띠로 클릭을 가로챈다.
       닫히면 여백을 없애고 클릭을 통과시킨다. invisible 은 안의 닫기 버튼이 Tab 포커스를 받지 않게 한다 */
    <aside
      aria-hidden={!isOpen}
      className={`z-20 h-h-main-container transition-all duration-300 ease-in-out overflow-hidden flex flex-col gap-[24px] ${
        isOpen
          ? "w-[398px] min-w-[398px] opacity-100 border-l py-[16px] px-[24px]"
          : "w-0 min-w-0 opacity-0 pointer-events-none invisible"
      }`}
      style={{
        background: "rgba(252, 252, 252, 0.70)",
      }}
    >
      <div className="flex justify-between items-end">
        <Text variant="title-md">
          {type === "answer-reference"
            ? "Citation"
            : type === "paper-titles"
            ? "Papers"
            : "Reference"}
        </Text>
        <button
          className="w-[40px] h-[40px] flex justify-center items-center"
          aria-label="출처 패널 닫기"
          onClick={toggleAnswerSideBar}
        >
          <CloseIcon color="#333333" />
        </button>
      </div>
      <div className="overflow-y-auto h-full flex flex-col">
        {isKb ? (
          <Suspense fallback={null}>
            <KbCitationContent items={items} knowledgeContent={knowledgeContent} externalRef={list} />
          </Suspense>
        ) : (
          list
        )}
      </div>
    </aside>
  );
};

export default AnswerSourceSideBar;
